import 'js-polyfills/dom.js';
import '@babel/polyfill';
import Vue from 'vue';
import Vuetify from 'vuetify';
import { Root } from '@/pages';
import router from '@/router';
import store from '@/store';
import vi8Next from '@/lang';
import Vue2Filters from 'vue2-filters';
import VueMoment from 'vue-moment';
import Vuelidate from 'vuelidate';
import objectFitImages from 'object-fit-images';
import { contentfulPlugin, sync } from '@/contentful';
import contentAreaMap from '@/contentful/content-area-map';
import { focus, inputMask, transaction, addLabel } from '@/directives';
import { Auth } from '@aws-amplify/auth';
import Cookies from 'js-cookie';

import {
  DisplayDate,
  numberMasks,
  transactionFormat,
  variableReplace,
  zipCode,
  phoneObfuscate,
  emailObfuscate,
  makePositive,
  customMoment,
  Phone,
  DisplayPhoneNumber,
} from '@/filters';

// Dynamic Import function allows us to use env variable to point to config
// Details here: https://github.com/tc39/proposal-dynamic-import
// Have to pull out the process reference, can't use it in the ${...} below.
const BRANDING_DIR = process.env.VUE_APP_BRANDING_DIR;
import defaultColors from '@/assets/colors.json';
import(`@/${BRANDING_DIR}/assets/colors.json`)
  .then((colors) => {
    const themeColors = Object.assign(defaultColors, colors.default);
    bootstrapVue(themeColors);
  })
  .catch(() => {
    bootstrapVue(defaultColors);
  });

function bootstrapVue(themeColors) {
  const CONTENT_PROMISES = [contentAreaMap.getContentAreaMap(), sync()];
  Promise.all(CONTENT_PROMISES).then((contentResponses) => {
    // Content
    Vue.use(vi8Next, { contentful_keys: contentResponses[1] });
    Vue.use(contentfulPlugin, router);

    // Bootstrap Vuetify
    // TODO Remove casting. More recent 1.5.x versions have improved types.
    Vue.use(Vuetify as any, {
      theme: themeColors,
    });

    // Helper libraries
    Vue.use(Vue2Filters);
    Vue.use(VueMoment);
    Vue.use(Vuelidate);

    // Directives
    Vue.directive('focus', focus);
    Vue.directive('inputMask', inputMask);
    Vue.directive('transaction', transaction);
    Vue.directive('addLabel', addLabel);

    // Filters
    Vue.filter('displayDate', DisplayDate);
    Vue.filter('last4Digits', numberMasks.last4Digits);
    Vue.filter('maskedLast4Digits', numberMasks.maskedLast4Digits);
    Vue.filter('phone', Phone);
    Vue.filter('transactionFormat', transactionFormat);
    Vue.filter('varReplace', variableReplace.varReplace);
    Vue.filter('zipCode', zipCode);
    Vue.filter('phoneObfuscate', phoneObfuscate);
    Vue.filter('emailObfuscate', emailObfuscate);
    Vue.filter('makePositive', makePositive);
    Vue.filter('customMoment', customMoment);
    Vue.filter('displayPhoneNumber', DisplayPhoneNumber);

    // Disable production tip in console
    Vue.config.productionTip = false;

    objectFitImages();

    // For non-co-branded SSO Partners, check identity_provider param being passed to set client Id
    const URL = window.location.toString();
    const authCode = URL.match(/code=([^&]+)/);
    const identityProvider = URL.match(/identity_provider=([^&]+)/);
    // We are coming to the app fresh and any existing clientid should be removed.
    if (!identityProvider && !authCode && !Cookies.get('idToken')) {
      localStorage.removeItem('clientId');
    }
    let clientId = localStorage.getItem('clientId');
    if (identityProvider) {
      if (identityProvider[1].toUpperCase() === 'HIGHMARK') {
        clientId = process.env.VUE_APP_COGNITO_OAUTH_CLIENT_ID_HIGHMARK;
      } else {
        clientId = process.env.VUE_APP_COGNITO_OAUTH_CLIENT_ID;
      }
      localStorage.setItem('clientId', clientId);
    } else if (!authCode && (!clientId || clientId === 'undefined')) {
      clientId = process.env.VUE_APP_COGNITO_USER_POOL_WEB_CLIENT;
      localStorage.setItem('clientId', clientId);
    }

    // Amplify
    Auth.configure({
      endpoint: process.env.VUE_APP_COGNITO_PROXY,
      // Amazon Cognito Region
      region: process.env.VUE_APP_COGNITO_REGION,
      // Amazon Cognito User Pool ID
      userPoolId: process.env.VUE_APP_COGNITO_USER_POOL_ID,
      // Amazon Cognito Web Client ID
      userPoolWebClientId: clientId,
      authenticationFlowType: process.env.VUE_APP_COGNITO_AUTH_FLOW_TYPE,
      oauth: {
        domain: process.env.VUE_APP_COGNITO_OAUTH_URL,
        scope: [
          'openid',
        ],
        redirectSignIn: `${window.location.origin}/portal/login`,
        redirectSignOut: `${window.location.origin}/portal/logged-out?signed_out=true&federated_sign_out=true`,
        responseType: 'code',
      },
      federationTarget: 'COGNITO_USER_POOLS',
    });

    const VUE_APP = Vue.extend(Root);
    new VUE_APP({
      router,
      store,
    }).$mount('#root');
  });
}

declare global {
  interface Window {
    [key: string]: any;
  }
}
